/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ChatSessionLink = {
  /**
   * Email of the person who initiated the move, when known.
   */
  actorEmail?: string | undefined;
  /**
   * Whether the continuation exists as a captured chat the caller can read, i.e. whether the child side is navigable.
   */
  childCaptured: boolean;
  /**
   * Chat id of the child session in this relationship. Absent when its session id was unknowable when the relationship was recorded (e.g. Cursor mints ids server-side) — or when the caller's visibility scope cannot read the child, which is deliberately indistinguishable.
   */
  childChatId?: string | undefined;
  /**
   * Title of the child chat, when it has been captured and titled and the caller's visibility scope can read it.
   */
  childTitle?: string | undefined;
  /**
   * When the move was recorded.
   */
  createdAt: Date;
  /**
   * Hostname of the machine the move happened on, when known.
   */
  deviceHostname?: string | undefined;
  /**
   * Link kind: move for continuations, recall for recalled context, or subagent for a helper session.
   */
  kind: string;
  /**
   * Whether the parent exists as a captured chat the caller can read, i.e. whether the parent side is navigable.
   */
  parentCaptured: boolean;
  /**
   * Chat id of the parent session in this relationship. Absent when the caller's visibility scope cannot read the parent — a masked end exposes no identity, matching parent_captured.
   */
  parentChatId?: string | undefined;
  /**
   * Title of the parent chat, when it has been captured and titled and the caller's visibility scope can read it.
   */
  parentTitle?: string | undefined;
  /**
   * Harness the session originated in, when known.
   */
  sourceSurface?: string | undefined;
  /**
   * Harness the session was moved to (e.g. cursor, codex, claude-code).
   */
  targetHarness: string;
};

/** @internal */
export const ChatSessionLink$inboundSchema: z.ZodMiniType<
  ChatSessionLink,
  unknown
> = z.pipe(
  z.object({
    actor_email: z.optional(z.string()),
    child_captured: z.boolean(),
    child_chat_id: z.optional(z.string()),
    child_title: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    device_hostname: z.optional(z.string()),
    kind: z.string(),
    parent_captured: z.boolean(),
    parent_chat_id: z.optional(z.string()),
    parent_title: z.optional(z.string()),
    source_surface: z.optional(z.string()),
    target_harness: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "actor_email": "actorEmail",
      "child_captured": "childCaptured",
      "child_chat_id": "childChatId",
      "child_title": "childTitle",
      "created_at": "createdAt",
      "device_hostname": "deviceHostname",
      "parent_captured": "parentCaptured",
      "parent_chat_id": "parentChatId",
      "parent_title": "parentTitle",
      "source_surface": "sourceSurface",
      "target_harness": "targetHarness",
    });
  }),
);

export function chatSessionLinkFromJSON(
  jsonString: string,
): SafeParseResult<ChatSessionLink, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ChatSessionLink$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ChatSessionLink' from JSON`,
  );
}
